/**
 * @fileoverview Business profile management actions and hooks
 * @module BusinessActions
 */

import apiClient from "../utils/apiClient";
import useSWR from "swr";

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */

/**
 * Hook that returns the business data for the current user client
 * @function
 * @name useBusiness
 * @memberof module:BusinessActions
 * @param {number} clientId - ID for the client
 * @returns {SWRObject} SWR Object containing business data
 */
export const useBusiness = (clientId) => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error, mutate } = useSWR(`clients/pro/${clientId}`, fetcher);

  return { data, isLoading: !error && !data, error, mutate };
};

/**
 * Updates business information for a client
 * @function
 * @name updateBusiness
 * @memberof module:BusinessActions
 * @param {number} clientId - ID for the client
 * @param {Object} data - Data to update for the business
 * @returns {Promise<void>}
 */
export const updateBusiness = async (clientId, data) => {
  try {
    await apiClient.patch(`/clients/update/pro/${clientId}`, data);
  } catch (err) {
    $log.error(err);
  }
};

/**
 * @typedef {Object} SWRObject
 * @property {object} data - The data returned by the swr call
 * @property {boolean} isLoading - State of the swr call
 */

/**
 * Hook that returns the permalink for the client
 * @function
 * @name usePermaLink
 * @memberof module:BusinessActions
 * @returns {SWRObject} SWR Object containing permalink data
 */

export const usePermaLink = () => {
  const fetcher = (url) => apiClient.get(url).then((res) => res.data);
  const { data, error, mutate } = useSWR("/clients/permalink", fetcher);
  return { data, isLoading: !error && !data, error, mutate };
};

/**
 * Updates the permalink for clients
 * @function
 * @name updatePermaLink
 * @memberof module:BusinessActions
 * @param {Object} data - Data for the permalink update
 * @returns {Promise<void>}
 */

export const updatePermaLink = async (data) => {
  try {
    await apiClient.patch("/clients/permalink", data);
  } catch (err) {
    $log.error(err);
  }
};
